/**
 * FileDrop: drop a file here, or choose one. For cover photos, imports and bundles (cockatrice-ui
 * `src/ui/FileDrop.jsx`, ported to hyperscript; PLAN.md §0).
 * The "Choose file…" button is the keyboard and phone path; dragging is a desktop extra.
 * accept: same as <input accept>, e.g. "image/jpeg,image/png" or ".cockatrice,.docx".
 * onFiles(File[]) with the accepted files; onReject(File[]) with the ones that did not match.
 */
import './css/file-drop.css';
import { createSignal, createUniqueId, mergeProps, splitProps } from 'solid-js';
import Icon from './Icon.ts';
import type { IconName } from './Icon.ts';
import { component, el, spread, when } from './solid.ts';
import type { Attributes, Child, On } from './solid.ts';
import { uiText } from './text.ts';

export interface FileDropProps extends Attributes {
  /** Main line. Default "Drag a file here"; hidden on touch screens. */
  readonly title?: Child;
  /** Formats and sizes. */
  readonly hint?: Child;
  /** Button text. Default "Choose file…". */
  readonly buttonLabel?: string | undefined;
  /** Icon in the circle. Default "upload". */
  readonly icon?: IconName | undefined;
  /** Accepted types, as <input accept>. Dropped files are checked too. */
  readonly accept?: string | undefined;
  /** Allow several files. */
  readonly multiple?: boolean | undefined;
  /** Called with the accepted files. */
  readonly onFiles?: ((files: File[]) => void) | undefined;
  /** Called with files that didn't match accept. */
  readonly onReject?: ((files: File[]) => void) | undefined;
  /** Error message. */
  readonly error?: Child;
  /** Show chosen file names. Default true. */
  readonly showFiles?: boolean | undefined;
  /** md (default) or sm (one row). */
  readonly size?: 'sm' | 'md' | undefined;
  /** Disables the zone. */
  readonly disabled?: boolean | undefined;
  /** Id of the hidden file input. */
  readonly id?: string | undefined;
  /** Name of the file input. */
  readonly name?: string | undefined;
  /** Extra class. */
  readonly class?: string | undefined;
  /** Extra content, such as a preview. */
  readonly children?: Child;
}

const matches = (file: File, accept: string | undefined): boolean => {
  if (!accept) return true;
  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();
  return accept
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean)
    .some((rule) => (rule.startsWith('.') ? name.endsWith(rule) : rule.endsWith('/*') ? type.startsWith(rule.slice(0, -1)) : type === rule));
};

export default function FileDrop(props: FileDropProps): Child {
  const p = mergeProps(
    {
      get title() {
        return uiText()('ui.file-drop.title');
      },
      get buttonLabel() {
        return uiText()('ui.file-drop.button');
      },
      icon: 'upload',
      showFiles: true,
      size: 'md',
    } as const,
    props,
  );
  const [local, rest] = splitProps(p, [
    'id',
    'title',
    'hint',
    'buttonLabel',
    'icon',
    'accept',
    'multiple',
    'onFiles',
    'onReject',
    'disabled',
    'error',
    'showFiles',
    'size',
    'class',
    'children',
    'name',
  ]);
  const uid = createUniqueId();
  const id = (): string => local.id ?? `${uid}-input`;
  const [over, setOver] = createSignal(false);
  const [names, setNames] = createSignal<string[]>([]);
  let input: HTMLInputElement | undefined;
  let depth = 0;

  const take = (list: FileList | null | undefined): void => {
    const files = Array.from(list ?? []);
    if (!files.length) return;
    const ok = files.filter((f) => matches(f, local.accept));
    const bad = files.filter((f) => !matches(f, local.accept));
    const kept = local.multiple === true ? ok : ok.slice(0, 1);
    if (kept.length) {
      setNames(kept.map((f) => f.name));
      local.onFiles?.(kept);
    }
    if (bad.length) local.onReject?.(bad);
  };

  const onDragEnter = (event: DragEvent): void => {
    if (local.disabled === true) return;
    event.preventDefault();
    depth++;
    setOver(true);
  };
  const onDragLeave = (): void => {
    depth = Math.max(0, depth - 1);
    if (!depth) setOver(false);
  };
  const onDrop = (event: DragEvent): void => {
    event.preventDefault();
    depth = 0;
    setOver(false);
    if (local.disabled !== true) take(event.dataTransfer?.files);
  };

  const cls = (): string =>
    [
      'ui-file-drop',
      `ui-file-drop--${local.size}`,
      over() && 'is-over',
      Boolean(local.error) && 'is-invalid',
      local.disabled === true && 'is-disabled',
      local.class,
    ]
      .filter(Boolean)
      .join(' ');

  return el(
    'div',
    spread(rest, {
      class: cls,
      onDragEnter,
      onDragOver: (event: DragEvent) => {
        if (local.disabled !== true) event.preventDefault();
      },
      onDragLeave,
      onDrop,
      onClick: (event: MouseEvent) => {
        if (local.disabled === true || (event.target instanceof Element && event.target.closest('button, a, input'))) return;
        input?.click();
      },
    }),
    el('input', {
      ref: (node: HTMLInputElement) => {
        input = node;
      },
      id,
      name: () => local.name,
      type: 'file',
      class: 'sr-only',
      tabindex: '-1',
      'aria-hidden': 'true',
      accept: () => local.accept,
      multiple: () => local.multiple,
      disabled: () => local.disabled,
      onChange: (event: On<Event, HTMLInputElement>) => {
        take(event.currentTarget.files);
        event.currentTarget.value = '';
      },
    }),
    el(
      'span',
      { class: 'ui-file-drop__icon', 'aria-hidden': 'true' },
      component(Icon, {
        get name() {
          return local.icon;
        },
        size: 'lg',
      }),
    ),
    el(
      'div',
      { class: 'ui-file-drop__text' },
      el('p', { class: 'ui-file-drop__title', id: `${uid}-title` }, () => (over() ? uiText()('ui.file-drop.drop') : local.title)),
      when(
        () => Boolean(local.hint),
        () => el('p', { class: 'ui-file-drop__hint', id: `${uid}-hint` }, () => local.hint),
      ),
    ),
    el(
      'button',
      {
        type: 'button',
        class: 'ui-file-drop__button',
        disabled: () => local.disabled,
        'aria-describedby': () => [Boolean(local.hint) && `${uid}-hint`, Boolean(local.error) && `${uid}-err`].filter(Boolean).join(' ') || undefined,
        onClick: () => input?.click(),
      },
      () => local.buttonLabel,
    ),
    when(
      () => local.showFiles && names().length > 0,
      () => el('p', { class: 'ui-file-drop__files', 'aria-live': 'polite' }, component(Icon, { name: 'check', size: 'sm' }), el('span', {}, () => names().join(', '))),
    ),
    when(
      () => Boolean(local.error),
      () => el('p', { class: 'ui-file-drop__error', id: `${uid}-err` }, component(Icon, { name: 'warn', size: 'sm' }), el('span', {}, () => local.error)),
    ),
    () => local.children,
  );
}
